import { Children, type CSSProperties, type ReactNode } from 'react';
import { SkeletonBar } from '@/ui/feedback/Loading';
import styles from './KpiCard.module.css';

export interface KpiGridProps {
  /** Names the row for a screen reader: "Doctors in numbers". */
  label: string;
  /** The cards, each a `KpiCard`. */
  children: ReactNode;
}

/**
 * A row of counts above a list. The request of 6 October 2026 (fifth),
 * section 1: the cards are the same size as each other at every width.
 *
 * Two columns on a phone, and from 760px, where a table stops being cards, as
 * many equal columns as there are cards, so five counts are one row of five
 * and four are one row of four. Every card of a row is as tall as the tallest,
 * and a label that wraps does not move the figure under it.
 */
export function KpiGrid({ label, children }: KpiGridProps) {
  const count = Math.max(1, Children.count(children));
  return (
    <div
      className={styles.grid}
      role="group"
      aria-label={label}
      style={{ '--kpi-count': String(count) } as CSSProperties}
    >
      {children}
    </div>
  );
}

export interface KpiCardProps {
  label: string;
  /**
   * The count. Undefined while it is on its way: the card keeps its shape and
   * draws a bar. Null for a count that could not be read: the card keeps its
   * shape and draws nothing, rather than a bar that waits for ever.
   */
  value: number | null | undefined;
  /**
   * Makes the card something to press: a button that says whether it is
   * pressed. Without it the card is a figure to read.
   */
  onPress?: () => void;
  /** Whether the list under the row is the one this card counts. Only with `onPress`. */
  pressed?: boolean;
  /** How the figure is said, where it is not a plain count: money says its currency. */
  format?: (value: number) => string;
}

/**
 * One count: what it counts, and the figure. The request of 6 October 2026
 * (fifth), section 1.
 *
 * Drawn from the first paint, before its figure is known, with a bar where the
 * figure will be and the room the figure takes: nothing under the row moves
 * when the counts arrive. With `onPress` the card is a button with
 * `aria-pressed`, tinted while it is pressed as every chosen thing is, and
 * what pressing it does is its owner's: narrowing the list below to what the
 * card counts, and widening it again.
 */
export function KpiCard({ label, value, onPress, pressed = false, format }: KpiCardProps) {
  const content = (
    <>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>
        {value === undefined ? (
          <SkeletonBar width="46%" height={18} />
        ) : value === null ? null : format === undefined ? (
          value.toLocaleString()
        ) : (
          format(value)
        )}
      </span>
    </>
  );

  if (onPress === undefined) return <div className={styles.card}>{content}</div>;

  return (
    <button
      type="button"
      className={`${styles.card} ${styles.press} ${pressed ? styles.pressed : ''}`}
      aria-pressed={pressed}
      onClick={onPress}
    >
      {content}
    </button>
  );
}
